import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { auth } from '../firebase';
import { CandidateActionsModal } from '../metronic/components/CandidateActionsModal';
import { CandidateTableRow } from '../metronic/components/CandidateTableRow';
import { ExternalCandidateCard } from '../metronic/components/ExternalCandidateCard';
import { PaginationControls } from '../metronic/components/PaginationControls';
import { PeopleSearchMetrics } from '../metronic/components/PeopleSearchMetrics';
import { SearchFiltersCard } from '../metronic/components/SearchFiltersCard';
import { ViewToggle } from '../metronic/components/ViewToggle';
import { Header } from '../metronic/layouts/Header';
import { Sidebar } from '../metronic/layouts/Sidebar';
/**
 * People Search Page - External Search (Apollo.io)
 * Modernized with Metronic components and layout
 *
 * All functionality preserved from original PeopleSearch.jsx
 */
const PeopleSearch = () => {
  // Sidebar state (matching Conversation page pattern)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Search state
  const [searchParams, setSearchParams] = useState({
    jobTitles: '',
    locations: '',
    keywords: '',
    emailStatus: 'verified',
    perPage: '25'
  });

  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [evaluationResults, setEvaluationResults] = useState(null);
  const [error, setError] = useState(null);
  const [currentJobRequirements, setCurrentJobRequirements] = useState(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [processingCandidates, setProcessingCandidates] = useState(new Set());
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());

  // UI state
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalResults: 0,
    totalPages: 0,
    perPage: 25
  });

  // Calculate metrics
  const metrics = {
    totalFound: searchResults.length,
    processed: searchResults.filter(r => r.processed).length,
    processing: processingCandidates.size,
    avgMatch: searchResults.length > 0
      ? Math.round(searchResults.reduce((sum, r) => sum + (r.score || 0), 0) / searchResults.length)
      : 0
  };

  // Handlers
  const handleFilterChange = (newFilters) => {
    setSearchParams(newFilters);
  };

  const handleClearFilters = () => {
    setSearchParams({
      jobTitles: '',
      locations: '',
      keywords: '',
      emailStatus: 'verified',
      perPage: '25'
    });
    setSearchResults([]);
    setError(null);
    setSelectedProfile(null);
    setEvaluationResults(null);
    setProcessingCandidates(new Set());
    setSelectedCandidates(new Set());
    setPagination({
      currentPage: 1,
      totalResults: 0,
      totalPages: 0,
      perPage: 25
    });
  };

  const handleSearch = async (page = 1) => {
    // Validate that at least one search parameter is provided
    if (!searchParams.jobTitles.trim() && !searchParams.keywords.trim()) {
      setError('Please enter at least job titles or keywords to search');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Parse comma-separated values into arrays
      const titles = searchParams.jobTitles.trim()
        ? searchParams.jobTitles.split(',').map(title => title.trim()).filter(title => title)
        : [];

      const locations = searchParams.locations.trim()
        ? searchParams.locations.split(',').map(location => location.trim()).filter(location => location)
        : [];

      const response = await axios.post('/api/people/search', {
        titles: titles,
        locations: locations,
        email_status: [searchParams.emailStatus],
        keywords: searchParams.keywords.trim() || undefined,
        page: page,
        per_page: parseInt(searchParams.perPage)
      });

      const results = response.data.results.map(result => ({
        ...result,
        score: 0,
        status: result.processed ? 'processed' : 'processing'
      }));

      setSearchResults(results);

      // Update pagination state
      setPagination({
        currentPage: response.data.page || page,
        totalResults: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
        perPage: response.data.per_page || parseInt(searchParams.perPage)
      });

      if (results.length === 0) {
        setError('No results found. Try adjusting your search criteria.');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred while searching');
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessCandidate = useCallback(async (candidate) => {
    if (candidate.processed || processingCandidates.has(candidate.id)) {
      return;
    }

    setProcessingCandidates(prev => new Set(prev).add(candidate.id));
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated. Please log in again.');
      }

      const idToken = await user.getIdToken();

      const response = await axios.post('/api/people/process', {
        raw_person_data: candidate.raw_data
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.data.success) {
        const processedResult = response.data.result;

        setSearchResults(prevResults =>
          prevResults.map(result =>
            result.id === candidate.id
              ? {
                  ...result,
                  ...processedResult,
                  processed: true,
                  status: 'processed',
                  skills: processedResult.skills || [],
                  structured_at: processedResult.structured_at
                }
              : result
          )
        );

        console.log(`Successfully processed candidate: ${candidate.name}`);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred while processing the candidate');
      console.error('Processing error:', error);
    } finally {
      setProcessingCandidates(prev => {
        const newSet = new Set(prev);
        newSet.delete(candidate.id);
        return newSet;
      });
    }
  }, [processingCandidates]);

  const handleEvaluate = async (profile) => {
    // Ensure candidate is processed before evaluation
    if (!profile.processed) {
      await handleProcessCandidate(profile);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setIsLoading(true);
    setError(null);

    try {
      if (!currentJobRequirements) {
        const jobResponse = await axios.get('/get_latest_job');
        setCurrentJobRequirements(jobResponse.data);
      }

      const response = await axios.post('/api/people/evaluate', {
        person_id: profile.id,
        job_requirements: currentJobRequirements
      });

      const evaluation = response.data.evaluation;
      setEvaluationResults(evaluation);
      setSelectedProfile(profile);

      setSearchResults(prevResults =>
        prevResults.map(result =>
          result.id === profile.id
            ? { ...result, score: evaluation.score, match_percentage: evaluation.score }
            : result
        )
      );
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred while evaluating the profile');
      console.error('Evaluation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetEmail = async (profile) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/people/action', {
        person_id: profile.id,
        action: 'getEmail'
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      alert(`Email retrieved successfully! Check console for details.`);
      console.log('Email data:', response.data);
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred while getting email');
      console.error('Get email error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetPhone = async (profile) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/people/action', {
        person_id: profile.id,
        action: 'getPhone'
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      alert(`Phone retrieved successfully! Check console for details.`);
      console.log('Phone data:', response.data);
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred while getting phone');
      console.error('Get phone error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallAgent = async (profile) => {
    // Placeholder for call agent functionality
    alert(`Call agent functionality for ${profile.name} - Coming soon!`);
    console.log('Call agent for:', profile);
  };

  const handleSelectCandidate = (candidateId, isSelected) => {
    setSelectedCandidates(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(candidateId);
      } else {
        newSet.delete(candidateId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedCandidates(new Set(searchResults.map(r => r.id)));
    } else {
      setSelectedCandidates(new Set());
    }
  };

  // Process candidates when they are loaded (lazy processing)
  useEffect(() => {
    searchResults.forEach(result => {
      if (!result.processed && !processingCandidates.has(result.id) && result.raw_data) {
        handleProcessCandidate(result);
      }
    });
  }, [searchResults, handleProcessCandidate, processingCandidates]);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col" style={{ marginLeft: sidebarCollapsed ? '0' : '256px' }}>
        {/* Header */}
        <Header />

        {/* Page Content */}
        <main className="flex-1 p-6">
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Search Filters */}
          <SearchFiltersCard
            filters={searchParams}
            onFilterChange={handleFilterChange}
            onSearch={() => handleSearch(1)}
            onClear={handleClearFilters}
            isLoading={isLoading}
          />

          {/* Metrics (only show if we have results) */}
          {searchResults.length > 0 && (
            <PeopleSearchMetrics
              totalFound={metrics.totalFound}
              processed={metrics.processed}
              processing={metrics.processing}
              avgMatch={metrics.avgMatch}
            />
          )}

          {/* Results Section */}
          {searchResults.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 animate-fade-in">
              {/* Results Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    {/* View Toggle */}
                    <ViewToggle view={viewMode} onViewChange={setViewMode} />

                    {/* Bulk Actions (placeholder) */}
                    <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option>Bulk Actions</option>
                      <option>Process Selected</option>
                      <option>Evaluate Selected</option>
                      <option>Export Selected</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">
                      Showing {((pagination.currentPage - 1) * pagination.perPage) + 1} to{' '}
                      {Math.min(pagination.currentPage * pagination.perPage, pagination.totalResults)} of{' '}
                      {pagination.totalResults} results
                    </span>
                    <span className="text-sm text-gray-500">•</span>
                    <span className="text-sm text-gray-600">
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                  </div>
                </div>
              </div>

              {/* Table View */}
              {viewMode === 'table' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedCandidates.size === searchResults.length && searchResults.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Candidate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title & Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Skills
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Match %
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {searchResults.map((candidate) => (
                        <CandidateTableRow
                          key={candidate.id}
                          candidate={candidate}
                          isSelected={selectedCandidates.has(candidate.id)}
                          onSelect={(selected) => handleSelectCandidate(candidate.id, selected)}
                          onActionsClick={() => {
                            setSelectedProfile(candidate);
                            setIsActionModalOpen(true);
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Card View */}
              {viewMode === 'cards' && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {searchResults.map((candidate) => (
                      <ExternalCandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        onActionsClick={() => {
                          setSelectedProfile(candidate);
                          setIsActionModalOpen(true);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Pagination */}
              <PaginationControls
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalResults={pagination.totalResults}
                onPageChange={(page) => handleSearch(page)}
              />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && searchResults.length === 0 && !error && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="max-w-md mx-auto">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No search results yet
                </h3>
                <p className="text-gray-600">
                  Enter job titles or keywords above to search for candidates in the Apollo.io database.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Actions Modal */}
      <CandidateActionsModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        candidate={selectedProfile}
        onGetEmail={handleGetEmail}
        onGetPhone={handleGetPhone}
        onEvaluate={handleEvaluate}
        onCallAgent={handleCallAgent}
      />
    </div>
  );
};

export default PeopleSearch;

